import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Loader2, type LucideIcon } from "lucide-react";
import { cn } from "../lib/cn";

export type ButtonVariant =
  | "primary"
  | "crimson"
  | "secondary"
  | "ghost"
  | "subtle"
  | "danger"
  | "link";

export type ButtonSize = "xs" | "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Renders a spinner, disables the control, and keeps the label width. */
  loading?: boolean;
  icon?: LucideIcon;
  iconRight?: LucideIcon;
  fullWidth?: boolean;
  children?: ReactNode;
}

const base =
  "relative inline-flex items-center justify-center gap-2 font-medium whitespace-nowrap " +
  "rounded-lg select-none transition-[transform,box-shadow,background-color,color,border-color] " +
  "duration-200 ease-out active:scale-[0.98] " +
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500 " +
  "disabled:pointer-events-none disabled:opacity-50";

const variants: Record<ButtonVariant, string> = {
  /** Admin primary. Gold is the brand's action colour. */
  primary:
    "bg-gold-sheen text-ink-950 shadow-ir-sm hover:shadow-ir-gold hover:brightness-105 " +
    "border border-gold-600/40",
  /** Public primary — the customer buy action. Not used for destruction. */
  crimson:
    "bg-crimson-sheen text-white shadow-ir-sm hover:shadow-ir-crimson hover:brightness-110 " +
    "border border-crimson-700/40",
  secondary:
    "bg-surface text-content border border-line-strong hover:border-gold-400 " +
    "hover:bg-gold-50 dark:hover:bg-gold-950/40 shadow-ir-sm",
  ghost:
    "text-content-muted hover:text-content hover:bg-surface-sunken border border-transparent",
  subtle:
    "bg-surface-sunken text-content border border-transparent hover:bg-gold-100 " +
    "dark:hover:bg-gold-950/50",
  danger:
    "bg-danger-500 text-white hover:bg-danger-600 shadow-ir-sm border border-danger-600/40",
  link:
    "text-gold-700 dark:text-gold-400 underline-offset-4 hover:underline px-0 h-auto",
};

const sizes: Record<ButtonSize, string> = {
  xs: "h-7 px-2.5 text-xs rounded-md",
  sm: "h-9 px-3 text-sm",
  md: "h-10 px-4 text-sm",
  lg: "h-12 px-6 text-base rounded-xl",
};

const iconSizes: Record<ButtonSize, number> = { xs: 14, sm: 15, md: 16, lg: 18 };

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "md",
    loading = false,
    icon: Icon,
    iconRight: IconRight,
    fullWidth = false,
    className,
    children,
    disabled,
    type = "button",
    ...props
  },
  ref,
) {
  const iconSize = iconSizes[size];

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        base,
        variants[variant],
        variant !== "link" && sizes[size],
        fullWidth && "w-full",
        className,
      )}
      {...props}
    >
      {loading && (
        <Loader2
          size={iconSize}
          className="absolute animate-spin"
          aria-hidden="true"
        />
      )}
      <span
        className={cn(
          "inline-flex items-center gap-2 transition-opacity",
          loading && "opacity-0",
        )}
      >
        {Icon && <Icon size={iconSize} aria-hidden="true" />}
        {children}
        {IconRight && <IconRight size={iconSize} aria-hidden="true" />}
      </span>
    </button>
  );
});

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  icon: LucideIcon;
  /** Required — icon-only controls must still be announced. */
  label: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
}

const iconButtonSizes: Record<ButtonSize, string> = {
  xs: "h-7 w-7 rounded-md",
  sm: "h-9 w-9",
  md: "h-10 w-10",
  lg: "h-12 w-12 rounded-xl",
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(
    {
      icon: Icon,
      label,
      variant = "ghost",
      size = "md",
      loading = false,
      className,
      disabled,
      type = "button",
      ...props
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type}
        title={label}
        aria-label={label}
        disabled={disabled || loading}
        className={cn(base, variants[variant], iconButtonSizes[size], "px-0", className)}
        {...props}
      >
        {loading ? (
          <Loader2 size={iconSizes[size]} className="animate-spin" aria-hidden="true" />
        ) : (
          <Icon size={iconSizes[size]} aria-hidden="true" />
        )}
      </button>
    );
  },
);
